import { useCallback, useRef } from 'react';
import { createSSEParser } from './sseParser';
import type { TextDeltaEvent, ToolStartEvent, ToolResultEvent, MessageDoneEvent, ErrorEvent, UIAction } from './types';

export interface SseStreamCallbacks {
  onSessionId?: (sessionId: string) => void;
  onTextDelta?: (delta: string) => void;
  // toolUseId is the Anthropic content-block id (`toolu_…`) propagated
  // by the proxy on every tool_start / tool_result. Optional because
  // legacy mocks and replay paths may not carry it; consumers should
  // fall back to tool-name + first-pending matching when undefined.
  // Required for correct pairing when a single agentic round emits N
  // parallel tool_use blocks (e.g. tag_assets_bulk over multiple
  // pages) — see chat-batched-ops-ui-handoff.md.
  onToolStart?: (tool: string, input: Record<string, any>, toolUseId?: string) => void;
  onToolResult?: (
    tool: string,
    result: Record<string, any>,
    uiAction?: UIAction,
    toolUseId?: string,
  ) => void;
  onDone?: (usage: { input_tokens: number; output_tokens: number; tool_calls?: number }) => void;
  onError?: (code: string, message: string) => void;
}

export function useSseStreamRunner() {
  const abortRef = useRef<AbortController | null>(null);

  const run = useCallback(
    async (sendRequest: (signal: AbortSignal) => Promise<Response>, callbacks: SseStreamCallbacks) => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await sendRequest(controller.signal);

        if (!response.ok) {
          let errorBody: any;
          try {
            errorBody = await response.json();
          } catch {
            errorBody = { error: 'request_failed', message: `HTTP ${response.status}` };
          }
          if (response.status === 503 && errorBody?.error === 'scheduling_disabled') {
            callbacks.onError?.('scheduling_disabled', 'Scheduling is not available in this environment.');
          } else {
            callbacks.onError?.(errorBody?.error || 'request_failed', errorBody?.message || `HTTP ${response.status}`);
          }
          return;
        }

        const reader = response.body!.getReader();
        const parser = createSSEParser((eventType, data) => {
          switch (eventType) {
            case 'session_id':
              callbacks.onSessionId?.(data.session_id);
              break;
            case 'text_delta': {
              const { delta } = data as TextDeltaEvent;
              callbacks.onTextDelta?.(delta);
              break;
            }
            case 'tool_start': {
              const { tool_use_id, tool, input } = data as ToolStartEvent;
              callbacks.onToolStart?.(tool, input || {}, tool_use_id);
              break;
            }
            case 'tool_result': {
              const { tool_use_id, tool, result, ui_action } = data as ToolResultEvent;
              callbacks.onToolResult?.(tool, result || {}, ui_action, tool_use_id);
              break;
            }
            case 'message_done': {
              const { usage } = data as MessageDoneEvent;
              callbacks.onDone?.(usage as any);
              break;
            }
            case 'error': {
              const { code, message } = data as ErrorEvent;
              callbacks.onError?.(code, message);
              break;
            }
          }
        });

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            parser.push(value);
          }
        } finally {
          // stream finished
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          callbacks.onError?.('stream_error', err.message || 'Connection failed');
        }
      } finally {
        abortRef.current = null;
      }
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  return { run, cancel };
}
